/**
 * Toast Component
 * Компонент для отображения toast-уведомлений в углу экрана
 */

import { useToastStore, Toast } from '../stores/toastStore';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';


interface ToastProps {
  toast: Toast;
  onJobClick?: (jobId: string) => void;
}

function ToastItem({ toast, onJobClick }: ToastProps) {
  const { removeToast } = useToastStore();

  const handleClick = () => {
    if (toast.jobId && onJobClick) {
      onJobClick(toast.jobId);
    } else if (toast.onClick) {
      toast.onClick();
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'error':
        return <XCircle className="h-6 w-6 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-6 w-6 text-yellow-500" />;
      case 'info':
        return <Info className="h-6 w-6 text-blue-500" />;
      default:
        return <Info className="h-6 w-6 text-gray-500" />;
    }
  };

  const getBgColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-900';
      case 'error':
        return 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-900';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-900';
      case 'info':
        return 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-900';
      default:
        return 'bg-gray-50 dark:bg-zinc-900 border-gray-200 dark:border-zinc-800';
    }
  };

  const getTextColor = () => {
    switch (toast.type) {
      case 'success':
        return 'text-green-900 dark:text-green-200';
      case 'error':
        return 'text-red-900 dark:text-red-200';
      case 'warning':
        return 'text-yellow-900 dark:text-yellow-200';
      case 'info':
        return 'text-blue-900 dark:text-blue-200';
      default:
        return 'text-gray-900 dark:text-zinc-100';
    }
  };

  const isClickable = toast.jobId || toast.onClick;

  return (
    <div
      className={`
        ${getBgColor()}
        border rounded-lg shadow-lg p-4 mb-3 min-w-[320px] max-w-[500px]
        transition-all duration-300 ease-in-out
        ${isClickable ? 'cursor-pointer hover:shadow-xl transform hover:scale-[1.02]' : ''}
        animate-slide-in-right
      `}
      onClick={handleClick}
      role={isClickable ? 'button' : 'alert'}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">{getIcon()}</div>
        <div className="ml-3 flex-1">
          <p className={`text-sm font-medium ${getTextColor()}`}>{toast.title}</p>
          <p className={`mt-1 text-sm ${getTextColor()} opacity-90`}>{toast.message}</p>
          {toast.jobId && (
            <p className="mt-2 text-xs text-gray-500 dark:text-zinc-400">Нажмите, чтобы открыть детали задачи</p>
          )}
          {!toast.jobId && toast.onClick && (
            <p className="mt-2 text-xs text-blue-600 dark:text-blue-400 underline">Нажмите для перехода →</p>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            removeToast(toast.id);
          }}
          className="ml-4 flex-shrink-0 text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300 transition-colors"
          aria-label="Закрыть"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

export default function ToastContainer({ onJobClick }: { onJobClick?: (jobId: string) => void }) {
  const { toasts } = useToastStore();

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onJobClick={onJobClick} />
      ))}
    </div>
  );
}

